<template>
    <div class="SidebarPanel__wrapper">
        <!-- Backdrop overlay -->
        <transition name="transition-fade">
            <button
                v-if="isOpen && !persistent"
                :class="['SidebarPanel__backdrop', { 'SidebarPanel__backdrop--visible': showMobileBackdrop }]"
                :aria-label="closeLabel"
                @click="$emit('close')"
            />
        </transition>

        <!-- Panel -->
        <div
            ref="focusTrapContainer"
            :dusk="dusk"
            :role="shouldTrapFocus ? 'dialog' : undefined"
            :aria-modal="shouldTrapFocus ? 'true' : undefined"
            :aria-label="shouldTrapFocus ? title : undefined"
            :class="panelClasses"
            :style="panelStyle"
        >
            <!-- Drag handle for mobile bottom sheets -->
            <button class="SidebarPanel__handle" :aria-label="closeLabel" @click="$emit('close')">
                <span class="SidebarPanel__handle-line" />
            </button>

            <!-- Header -->
            <div class="flex shrink-0 flex-col gap-1 px-4 py-3">
                <div :class="['flex', side === 'left' ? 'justify-end' : '']">
                    <button
                        class="SidebarPanel__collapse"
                        :title="collapseTooltip"
                        :aria-label="closeLabel"
                        @click="$emit('close')"
                    >
                        <svg
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                            stroke="currentColor"
                            width="18"
                            height="18"
                        >
                            <!-- X icon for mobile fullscreen -->
                            <path
                                v-if="isMobileFullscreen"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                            />
                            <!-- Double chevron right for right panel -->
                            <path
                                v-else-if="side === 'right'"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="m5.25 4.5 7.5 7.5-7.5 7.5m6-15 7.5 7.5-7.5 7.5"
                            />
                            <!-- Double chevron left for left panel -->
                            <path
                                v-else
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15-7.5 7.5 7.5 7.5"
                            />
                        </svg>
                    </button>
                </div>
                <span class="truncate text-[15px] font-semibold text-[var(--theme-text-color)]">
                    {{ title }}
                </span>
            </div>

            <!-- Toolbar slot (fixed, non-scrollable) -->
            <div v-if="$slots.toolbar" class="flex shrink-0 flex-col px-4">
                <slot name="toolbar" />
            </div>

            <!-- Content slot (scrollable) -->
            <div class="flex flex-1 flex-col overflow-y-auto p-4">
                <slot />
            </div>

            <!-- Footer slot (fixed, non-scrollable) -->
            <div v-if="$slots.footer" class="SidebarPanel__footer">
                <slot name="footer" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';

export default {
    name: 'SidebarPanel',
    mixins: [focusTrapMixin],

    props: {
        dusk: {
            type: String,
            required: false,
            default: null,
        },

        /**
         * Controls panel visibility.
         */
        isOpen: {
            type: Boolean,
            default: false,
        },
        /**
         * Header title text.
         */
        title: {
            type: String,
            default: '',
        },
        /**
         * Panel position: 'left' or 'right'.
         */
        side: {
            type: String,
            default: 'right',
            validator: value => ['left', 'right'].includes(value),
        },
        /**
         * Expands panel width from 320px to 480px.
         */
        expanded: {
            type: Boolean,
            default: false,
        },
        /**
         * Full viewport mode (100vw x 100vh), hides header/toolbar/footer.
         */
        fullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * When true, hides backdrop allowing interaction with reader content.
         */
        persistent: {
            type: Boolean,
            default: false,
        },
        /**
         * Mobile-only fullscreen mode (100vh, no rounded corners).
         */
        mobileFullscreen: {
            type: Boolean,
            default: false,
        },
        /**
         * CSS height for partial mobile view (e.g., '48vh', '50vh').
         */
        peekHeight: {
            type: String,
            default: null,
        },
        /**
         * Shows a semi-transparent overlay backdrop on mobile (like bottom sheets).
         * Only applies when on mobile view.
         */
        mobileBackdrop: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            focusTrapEnabled: false,
        };
    },

    computed: {
        ...mapState(['ui', 'localizations', 'lang']),
        ...mapGetters(['onlyMobileVisible']),

        shouldTrapFocus() {
            // TODO: review this when sidebar left is persistent
            // return !this.persistent && this.isOpen;
            return this.isOpen;
        },

        closeLabel() {
            return this.localizations?.[this.lang]?.tooltips?.close;
        },

        isMobileFullscreen() {
            return this.mobileFullscreen && this.onlyMobileVisible;
        },

        showMobileBackdrop() {
            return this.mobileBackdrop && this.onlyMobileVisible;
        },

        panelStyle() {
            if (this.fullscreen || this.isMobileFullscreen) {
                return '';
            }
            if (this.peekHeight && this.onlyMobileVisible) {
                return `height: ${this.peekHeight}; max-height: ${this.peekHeight};`;
            }
            return 'top: calc(var(--ReaderHeaderHeight, 0px) + 4px); height: calc(100vh - var(--ReaderHeaderHeight, 0px) - 8px);';
        },

        panelClasses() {
            return [
                'SidebarPanel',
                this.side === 'right' ? 'SidebarPanel--right' : 'SidebarPanel--left',
                {
                    'is-open': this.isOpen,
                    'SidebarPanel--expanded': this.expanded,
                    'SidebarPanel--fullscreen': this.fullscreen,
                    'SidebarPanel--mobile-fullscreen': this.isMobileFullscreen,
                },
            ];
        },

        collapseTooltip() {
            return 'Collapse panel';
        },
    },

    watch: {
        shouldTrapFocus: {
            immediate: true,
            handler(shouldTrap) {
                this.focusTrapEnabled = shouldTrap;
            },
        },
    },

    methods: {
        onFocusTrapEscape() {
            this.$emit('close');
        },
    },
};
</script>

<style>
.SidebarPanel__wrapper {
    @apply relative;
}

.SidebarPanel__backdrop {
    @apply fixed left-0 z-[49] w-full cursor-default border-none bg-transparent;
    top: var(--ReaderHeaderHeight, 0px);
    height: calc(100% - var(--ReaderHeaderHeight, 0px));
}

.SidebarPanel__backdrop:focus {
    @apply outline-none;
}

.SidebarPanel__backdrop:focus-visible {
    @apply outline-none ring-2 ring-inset ring-blue-500;
}

.SidebarPanel__backdrop--visible {
    @apply bg-black/40;
}

/* Panel base styles */
.SidebarPanel {
    @apply pointer-events-none invisible fixed z-50 flex max-w-[calc(100vw-8px)] flex-col overflow-hidden rounded-xl;
    @apply w-[var(--sidebar-panel-width)];
    background: var(--theme-background-color, #fff);
    transition:
        transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease,
        width 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        visibility 0s 0.3s;
}

/* Panel open state */
.SidebarPanel.is-open {
    @apply pointer-events-auto visible;
    transition:
        transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s ease,
        width 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        visibility 0s 0s;
    box-shadow:
        0 4px 24px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(0, 0, 0, 0.05);
}

/* Right side positioning */
.SidebarPanel--right {
    @apply right-1;
    transform: translateX(calc(100% + 8px));
}

.SidebarPanel--right.is-open {
    @apply translate-x-0;
}

/* Left side positioning */
.SidebarPanel--left {
    @apply left-1;
    transform: translateX(calc(-100% - 8px));
}

.SidebarPanel--left.is-open {
    @apply translate-x-0;
}

/* Expanded width (480px) */
.SidebarPanel--expanded {
    @apply w-[480px];
}

/* Fullscreen mode */
.SidebarPanel--fullscreen {
    @apply !left-0 !right-0 !top-0 z-[60] !h-[100vh] w-full max-w-full rounded-none;
}

.SidebarPanel--fullscreen .SidebarPanel__header,
.SidebarPanel--fullscreen .SidebarPanel__toolbar,
.SidebarPanel--fullscreen .SidebarPanel__footer {
    @apply hidden;
}

.SidebarPanel--fullscreen .SidebarPanel__content {
    @apply px-8 py-6;
}

/* Collapse button */
.SidebarPanel__collapse {
    @apply flex shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent p-1.5;
    color: var(--theme-toolbar-icons-color);
    transition: background 0.15s;
}

.SidebarPanel__collapse:hover {
    @apply bg-black/5;
}

.SidebarPanel__collapse:focus {
    @apply outline-none;
}

.SidebarPanel__collapse:focus-visible {
    @apply outline-none ring-2 ring-blue-500 ring-offset-2;
}

/* Footer with border */
.SidebarPanel__footer {
    @apply flex shrink-0 flex-col border-t border-black/[0.06] px-4 py-3;
}

/* Drag handle - hidden on desktop */
.SidebarPanel__handle {
    @apply hidden;
}

.SidebarPanel__handle:focus {
    @apply outline-none;
}

.SidebarPanel__handle:focus-visible {
    @apply outline-none ring-2 ring-inset ring-blue-500;
}

.SidebarPanel__handle-line {
    @apply h-1 w-9 rounded-sm opacity-40;
    background: var(--theme-toolbar-icons-color, rgba(0, 0, 0, 0.25));
}

/* ============================================
   Theme Support
   ============================================ */

/* Dark mode */
.Reader[data-theme='dark'] .SidebarPanel.is-open {
    box-shadow:
        0 4px 24px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(255, 255, 255, 0.1);
}

.Reader[data-theme='dark'] .SidebarPanel__collapse:hover {
    background: rgba(255, 255, 255, 0.1);
}

.Reader[data-theme='dark'] .SidebarPanel__footer {
    border-top-color: rgba(255, 255, 255, 0.08);
}

/* High Contrast Dark mode */
.Reader[data-theme='high-contrast-dark'] .SidebarPanel.is-open {
    box-shadow: none;
    border: 1px solid #ffffff;
}

.Reader[data-theme='high-contrast-dark'] .SidebarPanel__collapse:hover {
    background: rgba(255, 255, 255, 0.15);
}

.Reader[data-theme='high-contrast-dark'] .SidebarPanel__footer {
    border-top-color: rgba(255, 255, 255, 0.08);
}

/* Sepia mode */
.Reader[data-theme='sepia'] .SidebarPanel__footer {
    border-top-color: rgba(101, 67, 33, 0.08);
}

/* ============================================
   Mobile Styles (≤570px)
   Bottom sheet behavior
   ============================================ */

@media screen and (max-width: 570px) {
    /* Show drag handle on mobile */
    .SidebarPanel__handle {
        @apply flex w-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent;
        padding: 10px 0 2px;
    }

    /* Hide collapse button on mobile (use drag handle instead) */
    .SidebarPanel__collapse {
        @apply hidden;
    }

    /* Mobile fullscreen shows collapse button, hides handle */
    .SidebarPanel--mobile-fullscreen .SidebarPanel__handle {
        @apply hidden;
    }

    .SidebarPanel--mobile-fullscreen .SidebarPanel__collapse {
        @apply flex;
    }

    /* Panel slides from bottom */
    .SidebarPanel {
        @apply left-0 right-0 max-h-[85vh] w-full max-w-full;
        top: auto !important;
        bottom: 0 !important;
        border-radius: 12px 12px 0 0;
        transform: translateY(100%);
    }

    /* Override side positioning for mobile */
    .SidebarPanel--right,
    .SidebarPanel--left {
        @apply left-0 right-0;
        transform: translateY(100%);
    }

    .SidebarPanel--right.is-open,
    .SidebarPanel--left.is-open {
        @apply translate-y-0;
    }

    /* Mobile fullscreen mode */
    .SidebarPanel--mobile-fullscreen {
        @apply !bottom-0 !top-0 z-[60] max-h-screen rounded-none;
        height: 100vh !important;
    }
}

/* ============================================
   Fade transition for backdrop (Vue 2 syntax)
   ============================================ */

.transition-fade-enter-active,
.transition-fade-leave-active {
    @apply transition-opacity duration-300 ease-in-out;
}

.transition-fade-enter,
.transition-fade-leave-to {
    @apply opacity-0;
}
</style>
